import { useState } from "react";
import { FileText, ChevronRight, ChevronDown, Check, Loader2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { eur, fmtDate } from "@/lib/format";
import { ImportXmlButton } from "@/components/ImportXmlButton";

const TONES = {
  uscita: {
    border: "border-orange-200 dark:border-orange-500/30",
    hover: "hover:bg-orange-50/50 dark:hover:bg-orange-950/10",
    iconBg: "bg-orange-500/15 text-orange-600",
    count: "text-orange-600",
    amount: "text-red-600",
    btn: "bg-orange-500 hover:bg-orange-600",
    link: "text-orange-600",
    sign: "-",
  },
  entrata: {
    border: "border-emerald-200 dark:border-emerald-500/30",
    hover: "hover:bg-emerald-50/50 dark:hover:bg-emerald-950/10",
    iconBg: "bg-emerald-500/15 text-emerald-600",
    count: "text-emerald-600",
    amount: "text-emerald-600",
    btn: "bg-emerald-600 hover:bg-emerald-700",
    link: "text-emerald-600",
    sign: "+",
  },
};

export const FattureDaAssegnare = ({ tipo, title, subtitle, count, cantieri, categories, onChanged, showImport }) => {
  const navigate = useNavigate();
  const t = TONES[tipo];
  const placeholderCat = tipo === "entrata" ? "entrata_da_classificare" : "da_classificare";
  const catList = (tipo === "entrata" ? (categories?.entrate || []) : (categories?.uscite || [])).filter((c) => c.key !== placeholderCat);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get("/transactions", { tipo, categoria_key: placeholderCat, limit: 200 });
      setItems(res.items || []);
    } catch (e) {
      toast.error(e.message || "Caricamento non riuscito.");
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) fetchItems();
  };

  const setDraft = (id, field, value) =>
    setDrafts((d) => ({ ...d, [id]: { ...d[id], [field]: value } }));

  const assegna = async (tx) => {
    const d = drafts[tx.id] || {};
    const categoria_key = d.categoria_key || "";
    if (!categoria_key) return toast.error("Seleziona una categoria (obbligatoria).");
    setSaving((s) => ({ ...s, [tx.id]: true }));
    try {
      await api.patch(`/transactions/${tx.id}/assegna`, { cantiere_id: d.cantiere_id || null, categoria_key });
      setItems((list) => list.filter((x) => x.id !== tx.id));
      toast.success("Fattura classificata.");
      onChanged?.();
    } catch (e) {
      toast.error(e.message || "Classificazione non riuscita.");
    } finally {
      setSaving((s) => ({ ...s, [tx.id]: false }));
    }
  };

  const onImported = async () => {
    if (!open) setOpen(true);
    await fetchItems();
    onChanged?.();
  };

  return (
    <div className={`rounded-xl border-2 ${t.border} bg-card animate-fade-up overflow-hidden`}>
      <div className="flex items-center">
        <button
          onClick={toggle}
          data-testid={`assegna-card-${tipo}`}
          className={`flex-1 flex items-center justify-between gap-4 px-5 py-4 text-left transition-all ${t.hover}`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${t.iconBg} shrink-0`}>
              <FileText className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold">{title}</p>
              <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <div className="text-right">
              <p className={`text-2xl font-bold font-mono ${t.count}`} data-testid={`assegna-count-${tipo}`}>{count}</p>
              <p className="text-[10px] uppercase text-muted-foreground">da rendicontare</p>
            </div>
            {open ? <ChevronDown className="h-5 w-5 text-muted-foreground" /> : <ChevronRight className="h-5 w-5 text-muted-foreground" />}
          </div>
        </button>
        {showImport && (
          <div className="pr-4">
            <ImportXmlButton onImported={onImported} size="sm" />
          </div>
        )}
      </div>

      {open && (
        <div className="border-t border-border px-4 py-3" data-testid={`assegna-panel-${tipo}`}>
          {loading ? (
            <div className="py-6 flex items-center justify-center text-sm text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Caricamento...
            </div>
          ) : items.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Niente da classificare. {showImport ? "Importa un file XML per iniziare." : "Ottimo lavoro!"}
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((tx) => {
                const d = drafts[tx.id] || {};
                return (
                  <div
                    key={tx.id}
                    data-testid={`assegna-row-${tx.id}`}
                    className="flex flex-col lg:flex-row lg:items-center gap-3 rounded-lg border border-border bg-background/60 px-3 py-3"
                  >
                    <div className="min-w-0 lg:flex-1">
                      <p className="text-sm font-medium truncate">{tx.descrizione}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {fmtDate(tx.data)}{tx.fornitore_nome ? ` · ${tx.fornitore_nome}` : ""}{tx.imported ? " · da XML" : ""}
                      </p>
                    </div>
                    <p className={`text-sm font-semibold font-mono shrink-0 lg:w-28 lg:text-right ${t.amount}`}>
                      {t.sign}{eur(tx.importo)}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2 lg:shrink-0">
                      <Select value={d.cantiere_id || ""} onValueChange={(v) => setDraft(tx.id, "cantiere_id", v)}>
                        <SelectTrigger className="w-full sm:w-[180px]" data-testid={`assegna-cantiere-${tx.id}`}>
                          <SelectValue placeholder="Commessa (facoltativa)" />
                        </SelectTrigger>
                        <SelectContent>
                          {cantieri.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={d.categoria_key || ""}
                        onValueChange={(v) => setDraft(tx.id, "categoria_key", v)}
                      >
                        <SelectTrigger className="w-full sm:w-[180px]" data-testid={`assegna-categoria-${tx.id}`}>
                          <SelectValue placeholder="Categoria (obbligatoria)" />
                        </SelectTrigger>
                        <SelectContent>
                          {catList.map((c) => (
                            <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        onClick={() => assegna(tx)}
                        disabled={saving[tx.id]}
                        data-testid={`assegna-conferma-${tx.id}`}
                        className={`gap-1 text-white shrink-0 ${t.btn}`}
                      >
                        {saving[tx.id] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        Classifica
                      </Button>
                    </div>
                  </div>
                );
              })}
              <button
                onClick={() => navigate("/transazioni", { state: { fatture: tipo === "uscita", entrate: tipo === "entrata" } })}
                data-testid={`assegna-vedi-tutte-${tipo}`}
                className={`text-xs ${t.link} hover:underline pt-1`}
              >
                Apri in Entrate & Uscite →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
